'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/query';
import { PersistGate } from 'redux-persist/integration/react';
import type { AppStore } from './redux/store';
import { makeStore, persistor } from './redux/store';
import LoadingScreen from './components/loadingScreen';

/**
 * Interface representing the props for the StoreProvider.
 *
 * children - The content inside the StoreProvider wrapper.
 */
interface Props {
  readonly children: ReactNode;
}

/**
 * Returns a wrapper that provides data from the redux store.
 *
 * @param children - The content inside the wrapper to be returned.
 */
const StoreProvider = ({ children }: Props) => {
  const storeRef = useRef<AppStore | null>(null);

  if (!storeRef.current) storeRef.current = makeStore();

  useEffect(() => {
    if (storeRef.current != null) {
      const unsubscribe = setupListeners(storeRef.current.dispatch);
      return unsubscribe;
    }
    return undefined;
  }, []);

  return (
    <Provider store={storeRef.current}>
      <PersistGate loading={<LoadingScreen />} persistor={persistor}>
        {children}
      </PersistGate>
    </Provider>
  );
};

export default StoreProvider;
